"use client";

import { useEffect, useState } from "react";
import { MapPin, ShieldAlert } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { EventLocation } from "@/lib/types";

const MAPS_CONSENT_KEY = "maps-consent-accepted";

type Props = {
  location: EventLocation | null;
};

export function EventLocationBlock({ location }: Props) {
  const hasAddress = !!(location?.address_street || location?.address_zip || location?.address_city || location?.address_country);
  const addressLine2 = [location?.address_zip, location?.address_city].filter(Boolean).join(" ");

  const [mapsConsent, setMapsConsent] = useState(false);

  useEffect(() => {
    setMapsConsent(localStorage.getItem(MAPS_CONSENT_KEY) === "1");
  }, []);

  function handleConsentChange(checked: boolean) {
    setMapsConsent(checked);
    localStorage.setItem(MAPS_CONSENT_KEY, checked ? "1" : "0");
  }

  return (
    <Card className="min-h-[500px]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MapPin className="h-4 w-4" />
          Location
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {hasAddress ? (
          <address className="text-sm not-italic text-muted-foreground">
            {location?.address_street && <div>{location.address_street}</div>}
            {addressLine2 && <div>{addressLine2}</div>}
            {location?.address_country && <div>{location.address_country}</div>}
          </address>
        ) : (
          <p className="text-sm text-muted-foreground">Es ist noch keine Adresse hinterlegt.</p>
        )}
        {location?.maps_embed_url &&
          (mapsConsent ? (
            <div className="overflow-hidden rounded-md border">
              <iframe src={location.maps_embed_url} className="h-[360px] w-full" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
            </div>
          ) : (
            <div className="flex h-[360px] w-full flex-col items-center justify-center gap-4 rounded-md border bg-muted/30 p-6 text-center">
              <ShieldAlert className="h-8 w-8 text-muted-foreground" />
              <p className="max-w-xs text-xs text-muted-foreground">
                Beim Aktivieren wird eine Karte von Google Maps geladen. Dabei werden Daten (u.&nbsp;a. deine IP-Adresse) an Google übertragen. Es gelten die{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
                  Datenschutzbestimmungen von Google
                </a>
                .
              </p>
              <div className="flex items-center gap-2">
                <Switch id="maps-consent" checked={mapsConsent} onCheckedChange={handleConsentChange} />
                <Label htmlFor="maps-consent" className="cursor-pointer text-sm font-normal">
                  Google Maps aktivieren
                </Label>
              </div>
            </div>
          ))}
      </CardContent>
    </Card>
  );
}
